import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable, tap } from 'rxjs';

import {
  ConversationDetailResponse,
  ConversationListResponse,
  ConversationSearchResponse,
  ConversationUpdate,
  FeedbackRequest,
  FeedbackResponse,
  MessageListResponse,
  OverviewResponse,
  ResponseRequest,
} from './pc-chat-api.types';
import { streamSseText, tryParseJson } from './sse-client';
import { resolveBackendUrl, shouldIncludeCredentialsForUrl } from '../config/backend-urls';
import { AuthService } from '../auth/auth.service';
import { withAuthRetry } from '../auth/auth-refresh';

@Injectable({ providedIn: 'root' })
export class PcChatApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  getOverview(): Observable<OverviewResponse> {
    return this.http.get<OverviewResponse>('/api/v1/overview');
  }

  listConversations(page = 1, limit = 20): Observable<ConversationListResponse> {
    const params = new HttpParams().set('page', page).set('limit', limit);
    return this.http.get<ConversationListResponse>('/api/conversations', { params });
  }

  searchConversations(q: string, limit = 20): Observable<ConversationSearchResponse> {
    const params = new HttpParams().set('q', q).set('limit', limit);
    return this.http.get<ConversationSearchResponse>('/api/conversations/search', { params });
  }

  getConversation(conversationId: number): Observable<ConversationDetailResponse> {
    return this.http.get<ConversationDetailResponse>(`/api/conversations/${conversationId}`);
  }

  updateConversation(conversationId: number, update: ConversationUpdate): Observable<unknown> {
    return this.http.patch(`/api/conversations/${conversationId}`, update);
  }

  deleteConversation(conversationId: number): Observable<unknown> {
    return this.http.delete(`/api/conversations/${conversationId}`);
  }

  listMessages(conversationId: number, page = 1, limit = 100): Observable<MessageListResponse> {
    const params = new HttpParams().set('page', page).set('limit', limit);
    return this.http.get<MessageListResponse>(`/api/conversations/${conversationId}/messages`, {
      params,
    });
  }

  submitFeedback(conversationId: number, request: FeedbackRequest): Observable<FeedbackResponse> {
    return this.http.post<FeedbackResponse>(
      `/api/conversations/${conversationId}/feedback`,
      request,
    );
  }

  createResponse(request: ResponseRequest): Observable<unknown> {
    return this.http.post('/api/v1/responses', request);
  }

  stopConversation(conversationId: number): Observable<unknown> {
    return this.http.post(`/api/conversations/${conversationId}/stop`, {});
  }

  createResponseStream(request: ResponseRequest): Observable<unknown> {
    return withAuthRetry(this.auth, () => this.buildSseStream(request));
  }

  private buildSseStream(request: ResponseRequest): Observable<unknown> {
    const url = '/api/v1/responses';
    return streamSseText(resolveBackendUrl(url), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      credentials: shouldIncludeCredentialsForUrl(url) ? 'include' : undefined,
      body: JSON.stringify({ ...request, stream: true }),
    }).pipe(
      map((evt) => {
        const parsed = tryParseJson(evt.data);

        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const rec = parsed as Record<string, unknown>;
          if (evt.event && typeof rec['type'] !== 'string') return { ...rec, type: evt.event };
          return rec;
        }

        if (evt.event) {
          return { type: evt.event, text: parsed };
        }
        return parsed;
      }),
      tap((payload) => {
        if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
          const rec = payload as Record<string, unknown>;
          if (rec['type'] === 'error') {
            throw new Error(typeof rec['message'] === 'string' ? rec['message'] : 'Stream error');
          }
        }
      }),
    );
  }
}
